import { createRequestBuilder } from '@quickplay/foundation';
import { Extensions } from '../PlatformExtensions';
import { AuthorizationUtil } from '../AuthorizationUtil';
import { PlatformAuthorizer } from '../PlatformAuthorizer';
import { AdobePrimeTokenRequest } from '../modal/AdobePrimeTokenRequest';
import { AdobePrimeTokenResponse, AdobePrimetimeToken } from '../modal/AdobePrimeTokenResponse';
import { GeoLocation } from '../modal/GeoLocationResponse';

export const PlatformExtensions: Extensions = {
  getAdobePrimetimeToken(
    endpointUrl: string,
    deviceId: string,
    deviceInfo: string,
    platformAuthorizer: PlatformAuthorizer,
  ): Promise<string | Error> {
    return getAdobePrimetimeToken(endpointUrl, deviceId, deviceInfo, platformAuthorizer);
  },
  getAdobePrimetimeTokenV2(
    endpointUrl: string,
    deviceId: string,
    deviceInfo: string,
    userId: string,
    platformAuthorizer: PlatformAuthorizer,
    profileId?: string,
  ): Promise<AdobePrimetimeToken> {
    return getAdobePrimetimeTokenV2(endpointUrl, deviceId, deviceInfo, userId, platformAuthorizer, profileId);
  },
  getGeoLocation(
    endpointUrl: string,
    platformAuthorizer: PlatformAuthorizer,
    queryParameters?: { [key: string]: string },
  ): Promise<GeoLocation | Error> {
    return getGeoLocation(endpointUrl, platformAuthorizer, queryParameters);
  },
};

async function getAdobePrimetimeToken(
  endpointUrl: string,
  deviceId: string,
  deviceInfo: string,
  platform: PlatformAuthorizer,
): Promise<string | Error> {
  const request = createRequestBuilder<AdobePrimeTokenRequest>()
    .url(endpointUrl)
    .path('/platform/access/token')
    .post({
      deviceId: deviceId,
      deviceInfo: deviceInfo,
    })
    .build();
  try {
    const response = await AuthorizationUtil.makeAuthorizedRequest<AdobePrimeTokenRequest, AdobePrimeTokenResponse>(
      request,
      platform,
    );
    return response.data.token;
  } catch (error) {
    return Promise.reject(error);
  }
}

async function getAdobePrimetimeTokenV2(
  endpointUrl: string,
  deviceId: string,
  deviceInfo: string,
  userId: string,
  platform: PlatformAuthorizer,
  profileId?: string,
): Promise<AdobePrimetimeToken> {
  const payload: any = {
    deviceId: deviceId,
    deviceInfo: deviceInfo,
    userId: userId,
  };

  if (profileId) {
    payload.profileId = profileId;
  }

  const request = createRequestBuilder<AdobePrimeTokenRequest>()
    .url(endpointUrl)
    .path('/platform/access/token/v2')
    .post(payload)
    .build();
  try {
    const response = await AuthorizationUtil.makeAuthorizedRequest<AdobePrimeTokenRequest, AdobePrimeTokenResponse>(
      request,
      platform,
    );
    return {
      token: response.data.token,
      expiresIn: response.data.expires_in,
    };
  } catch (error) {
    return Promise.reject(error);
  }
}

async function getGeoLocation(
  endpointUrl: string,
  platform: PlatformAuthorizer,
  queryParameters: { [key: string]: string } = {},
): Promise<GeoLocation | Error> {
  const request = createRequestBuilder()
    .url(endpointUrl)
    .path('/device/location/lookup')
    .queryParameters(queryParameters)
    .get()
    .build();

  try {
    const response = await AuthorizationUtil.makeAuthorizedRequest<any, GeoLocation>(request, platform);
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
}
